import { type FC, useState } from 'react';
import { Button, Dialog, DialogTrigger, IconButton, Modal, ModalOverlay } from '@launchpad-ui/components';

import { type Account, type CustomRole, type Member } from '../../../model';
import { type ConsolidationData } from '../utils';

import { BaseRoleInfo } from './BaseRoleInfo';
import { CollapsibleSection } from './CollapsibleSection';
import { PolicyDisplay } from './PolicyDisplay';
import { RoleAttributesTable } from './RoleAttributesTable';
import { RoleInfoTable } from './RoleInfoTable';

import './ConsolidationModal.scss';
import './modal-scrolling.scss';

type ConsolidationModalProps = {
  member: Member;
  account: Account;
  customRoles: { [roleId: string]: CustomRole };
  customRolesWithTeamsForMember: (member: Member, customRoles: { [roleId: string]: CustomRole }) => unknown[];
  teamData: { [teamKey: string]: { roleAttributes: { [attributeName: string]: string[] } } };
  onConsolidate: (member: Member, account: Account) => Promise<ConsolidationData>;
  buttonText?: string;
};

type ConsolidationState = {
  isLoading: boolean;
  showResolved: boolean;
  data: ConsolidationData | null;
  expandedSections: {
    roleInfo: boolean;
    roleAttributes: boolean;
  };
};

export const ConsolidationModal: FC<ConsolidationModalProps> = ({
  member,
  account,
  onConsolidate,
  buttonText = 'Consolidate',
}) => {
  const [state, setState] = useState<ConsolidationState>({
    isLoading: false,
    showResolved: false,
    data: null,
    expandedSections: {
      roleInfo: false,
      roleAttributes: false,
    },
  });

  const handleConsolidateClick = async () => {
    setState((prev) => ({ ...prev, isLoading: true, data: null }));

    try {
      const consolidationData = await onConsolidate(member, account);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        data: consolidationData,
      }));
    } catch (error) {
      console.error('Failed to consolidate permissions:', error);
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handleToggleResolved = () => {
    setState((prev) => ({ ...prev, showResolved: !prev.showResolved }));
  };

  const handleToggleSection = (section: 'roleInfo' | 'roleAttributes') => {
    setState((prev) => ({
      ...prev,
      expandedSections: {
        ...prev.expandedSections,
        [section]: !prev.expandedSections[section],
      },
    }));
  };

  const renderContent = () => {
    if (state.isLoading || !state.data) {
      return (
        <div className="consolidationModal__loadingState">
          <div>Loading consolidated permissions...</div>
          <div className="consolidationModal__loadingMessage">Fetching team data and consolidating roles...</div>
        </div>
      );
    }

    const { consolidatedRole, resolvedConsolidatedRole, consolidationInfo, hasPlaceholders, mergedRoleAttributes } =
      state.data;

    const availableAttributes = Object.fromEntries(
      Object.entries(mergedRoleAttributes).filter(([_, values]) => values.length > 0),
    );

    return (
      <div>
        <h3 className="consolidationModal__title">Final Effective Permissions</h3>

        {consolidationInfo.hasBaseRole && consolidationInfo.baseRole && (
          <BaseRoleInfo baseRole={consolidationInfo.baseRole} />
        )}

        <PolicyDisplay
          title={`${state.showResolved ? 'Resolved ' : ''}Consolidated Policy`}
          policy={state.showResolved ? resolvedConsolidatedRole : consolidatedRole}
          baseRole={consolidationInfo.customRoleBaseRoles?.dominantBaseRole}
          showBaseRole={consolidationInfo.customRoleBaseRoles?.hasBaseRoles}
        />

        {hasPlaceholders && (
          <div className="consolidationModal__toggleContainer">
            <Button variant={state.showResolved ? 'primary' : 'default'} onPress={handleToggleResolved}>
              {state.showResolved ? 'Hide Resolved Consolidated Role' : 'Show Resolved Consolidated Role'}
            </Button>
          </div>
        )}

        <CollapsibleSection
          title={`Roles merged in this policy (${consolidationInfo.directCustomRoles.length + consolidationInfo.teamRoles.length})`}
          isExpanded={state.expandedSections.roleInfo}
          onToggle={() => handleToggleSection('roleInfo')}
        >
          <RoleInfoTable
            directCustomRoles={consolidationInfo.directCustomRoles}
            teamRoles={consolidationInfo.teamRoles}
          />
        </CollapsibleSection>

        {(Object.keys(availableAttributes).length > 0 ||
          (consolidationInfo.missingRoleAttributes && consolidationInfo.missingRoleAttributes.length > 0)) && (
          <CollapsibleSection
            title={`Role Attributes (${Object.keys(availableAttributes).length} available${consolidationInfo.missingRoleAttributes && consolidationInfo.missingRoleAttributes.length > 0 ? `, ${consolidationInfo.missingRoleAttributes.length} missing` : ''})`}
            isExpanded={state.expandedSections.roleAttributes}
            onToggle={() => handleToggleSection('roleAttributes')}
          >
            <RoleAttributesTable
              availableAttributes={availableAttributes}
              missingAttributes={consolidationInfo.missingRoleAttributes}
            />
          </CollapsibleSection>
        )}
      </div>
    );
  };

  return (
    <DialogTrigger>
      <Button variant="primary" size="small" onPress={handleConsolidateClick}>
        {buttonText}
      </Button>
      <ModalOverlay>
        <Modal size="large" variant="drawer" className="consolidation-modal-scrollable">
          <Dialog className="consolidationDrawer" aria-label={`Consolidated Permissions: ${member.email}`}>
            <IconButton
              icon="cancel"
              size="medium"
              variant="minimal"
              className="closeDrawer"
              slot="close"
              aria-label="Close drawer"
            />
            <div className="consolidationModal__content">{renderContent()}</div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};
